'use client';

import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Cell, Legend,
} from 'recharts';

// ─── Data ────────────────────────────────────────────────────────────────────

const revenueMonthly = [
  { month: 'Mar', gross: 42, fees: 6.3, payouts: 35.7 },
  { month: 'Apr', gross: 58, fees: 8.7, payouts: 49.3 },
  { month: 'May', gross: 51, fees: 7.65, payouts: 43.35 },
  { month: 'Jun', gross: 74, fees: 11.1, payouts: 62.9 },
  { month: 'Jul', gross: 89, fees: 13.35, payouts: 75.65 },
  { month: 'Aug', gross: 96, fees: 14.4, payouts: 81.6 },
];

const userGrowth = [
  { month: 'Mar', guests: 8200, hosts: 420 },
  { month: 'Apr', guests: 9100, hosts: 510 },
  { month: 'May', guests: 10400, hosts: 590 },
  { month: 'Jun', guests: 11800, hosts: 680 },
  { month: 'Jul', guests: 13200, hosts: 760 },
  { month: 'Aug', guests: 14832, hosts: 841 },
];

const bookingTrend = [
  { day: 'Mon', bookings: 12, revenue: 18 },
  { day: 'Tue', bookings: 19, revenue: 27 },
  { day: 'Wed', bookings: 15, revenue: 22 },
  { day: 'Thu', bookings: 24, revenue: 34 },
  { day: 'Fri', bookings: 31, revenue: 45 },
  { day: 'Sat', bookings: 28, revenue: 41 },
  { day: 'Sun', bookings: 22, revenue: 32 },
];

const reviewDistribution = [
  { name: '5 Stars', value: 52, color: '#22c55e' },
  { name: '4 Stars', value: 28, color: '#84cc16' },
  { name: '3 Stars', value: 10, color: '#f59e0b' },
  { name: '2 Stars', value: 6, color: '#f97316' },
  { name: '1 Star', value: 4, color: '#ef4444' },
];

const recentActivity = [
  { type: 'booking', text: 'New booking: Luxury Villa Kololo — NDT-2026-0821', time: '2 min ago', icon: '📅', dot: 'bg-purple-400' },
  { type: 'user', text: 'New host registered: James Ssekandi (Kampala)', time: '15 min ago', icon: '👤', dot: 'bg-blue-400' },
  { type: 'approval', text: 'Property pending review: Safari Tent Kidepo Valley', time: '32 min ago', icon: '⏳', dot: 'bg-amber-400' },
  { type: 'payment', text: 'Payout processed: UGX 2,500,000 to Grace Achieng', time: '1 hr ago', icon: '💳', dot: 'bg-green-400' },
  { type: 'review', text: 'New review flagged for moderation: 1-star complaint', time: '2 hr ago', icon: '⚠️', dot: 'bg-red-400' },
  { type: 'user', text: 'Guest account verified: Sarah Nakato (Nairobi)', time: '3 hr ago', icon: '✅', dot: 'bg-teal-400' },
];

const topProperties = [
  { name: 'Luxury Villa Kololo', host: 'James Ssekandi', bookings: 24, revenue: 'UGX 12.4M', rating: 4.9 },
  { name: 'Safari Lodge Murchison', host: 'David Ochieng', bookings: 19, revenue: 'UGX 9.8M', rating: 4.8 },
  { name: 'Lakeside Retreat Entebbe', host: 'Grace Achieng', bookings: 17, revenue: 'UGX 7.2M', rating: 4.7 },
  { name: 'City Apartment Kampala', host: 'Amina Hassan', bookings: 31, revenue: 'UGX 6.5M', rating: 4.5 },
  { name: 'Mountain Cabin Rwenzori', host: 'Ruth Kemigisha', bookings: 12, revenue: 'UGX 5.9M', rating: 4.9 },
];

// ─── KPI Cards ────────────────────────────────────────────────────────────────

const kpis = [
  { label: 'Total Users', value: '14,832', change: '+8.2%', up: true, icon: '👥', accent: 'from-blue-500 to-blue-600', light: 'bg-blue-50 text-blue-700' },
  { label: 'Active Listings', value: '3,241', change: '+5.1%', up: true, icon: '🏠', accent: 'from-violet-500 to-violet-600', light: 'bg-violet-50 text-violet-700' },
  { label: 'Bookings (Aug)', value: '1,089', change: '+12.4%', up: true, icon: '📅', accent: 'from-teal-500 to-teal-600', light: 'bg-teal-50 text-teal-700' },
  { label: 'Gross Revenue', value: 'UGX 96M', change: '+18.7%', up: true, icon: '💰', accent: 'from-[#4088C6] to-[#3070a6]', light: 'bg-[#4088C6]/10 text-[#4088C6]' },
  { label: 'Platform Fees', value: 'UGX 14.4M', change: '+18.7%', up: true, icon: '📊', accent: 'from-amber-500 to-amber-600', light: 'bg-amber-50 text-amber-700' },
  { label: 'Pending Approvals', value: '47', change: '-3', up: false, icon: '⏳', accent: 'from-orange-500 to-orange-600', light: 'bg-orange-50 text-orange-700' },
  { label: 'Flagged Reviews', value: '9', change: '+2', up: false, icon: '🚩', accent: 'from-amber-500 to-amber-600', light: 'bg-amber-50 text-amber-700' },
  { label: 'Avg. Rating', value: '4.7', change: '+0.1', up: true, icon: '⭐', accent: 'from-yellow-500 to-yellow-600', light: 'bg-yellow-50 text-yellow-700' },
];

// ─── Section Header ───────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle, badge }: { title: string; subtitle?: string; badge?: string }) {
  return (
    <div className="flex items-center justify-between mb-5">
      <div>
        <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
      </div>
      {badge && (
        <span className="text-xs font-semibold bg-[#4088C6]/10 text-[#4088C6] border border-[#4088C6]/20 px-3 py-1 rounded-full">{badge}</span>
      )}
    </div>
  );
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2.5 text-xs">
      <p className="font-bold text-gray-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="font-semibold">
          {p.name}: {typeof p.value === 'number' && p.name.toLowerCase().includes('revenue') ? `UGX ${p.value}M` : p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

type TimeRange = '7d' | '30d' | '6m';

export default function AdminOverview() {
  const [timeRange, setTimeRange] = useState<TimeRange>('6m');

  return (
    <div className="space-y-8">

      {/* ── Time Range Selector ── */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Unified analytics view — all key metrics in one place</p>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1">
          {(['7d', '30d', '6m'] as TimeRange[]).map((r) => (
            <button
              key={r}
              onClick={() => setTimeRange(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                timeRange === r ? 'bg-[#4088C6] text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {r === '7d' ? 'Last 7 days' : r === '30d' ? 'Last 30 days' : 'Last 6 months'}
            </button>
          ))}
        </div>
      </div>

      {/* ── KPI Grid ── */}
      <section>
        <SectionHeader title="Platform KPIs" subtitle="Core performance indicators at a glance" badge="Live" />
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {kpis.map((k) => (
            <div key={k.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col gap-2 hover:shadow-md transition-shadow">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-base ${k.light}`}>
                {k.icon}
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900 leading-tight">{k.value}</p>
                <p className="text-[10px] text-gray-400 leading-tight mt-0.5">{k.label}</p>
              </div>
              <div className={`text-[11px] font-bold ${k.up ? 'text-green-600' : 'text-red-500'}`}>
                {k.up ? '↑' : '↓'} {k.change}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Revenue Metrics ── */}
      <section>
        <SectionHeader title="Platform Revenue Metrics" subtitle="Gross revenue, platform fees, and host payouts (UGX Millions)" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          {[
            { label: 'Gross Revenue (Aug)', value: 'UGX 96M', sub: '+18.7% vs Jul', color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-100' },
            { label: 'Platform Fees (15%)', value: 'UGX 14.4M', sub: 'Retained this month', color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-100' },
            { label: 'Host Payouts', value: 'UGX 81.6M', sub: 'Disbursed to hosts', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-100' },
          ].map((s) => (
            <div key={s.label} className={`${s.bg} border ${s.border} rounded-2xl p-5`}>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-sm font-semibold text-gray-700 mt-1">{s.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
          <p className="text-xs font-semibold text-gray-500 mb-4 uppercase tracking-wide">6-Month Revenue Breakdown</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={revenueMonthly} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="gross" name="Gross Revenue" fill="#f43f5e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="fees" name="Platform Fees" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="payouts" name="Host Payouts" fill="#3b82f6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ── User Growth & Booking Trends ── */}
      <section>
        <SectionHeader title="User Growth Trends" subtitle="Guest and host registrations over 6 months" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* User Growth */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Guest & Host Growth</p>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[#4088C6] inline-block" />Guests</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400 inline-block" />Hosts</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={userGrowth}>
                <defs>
                  <linearGradient id="guestGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="hostGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="guests" name="Guests" stroke="#3b82f6" strokeWidth={2.5} fill="url(#guestGrad)" dot={{ fill: '#3b82f6', r: 3 }} />
                <Area type="monotone" dataKey="hosts" name="Hosts" stroke="#f43f5e" strokeWidth={2.5} fill="url(#hostGrad)" dot={{ fill: '#f43f5e', r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Trend */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Weekly Booking Volume</p>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={bookingTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="bookings" name="Bookings" stroke="#f43f5e" strokeWidth={2.5} dot={{ fill: '#f43f5e', r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {[
            { label: 'Total Guests', value: '13,991', change: '+8.4%', icon: '🧳', color: 'text-blue-600' },
            { label: 'Total Hosts', value: '841', change: '+10.7%', icon: '🏡', color: 'text-[#4088C6]' },
            { label: 'New This Month', value: '1,632', change: '+14.2%', icon: '🆕', color: 'text-teal-600' },
            { label: 'Verified Users', value: '11,204', change: '+6.1%', icon: '✅', color: 'text-green-600' },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{s.icon}</span>
                <span className={`text-xs font-bold text-green-600`}>↑ {s.change}</span>
              </div>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Reviews & Moderation Stats ── */}
      <section>
        <SectionHeader title="Reviews & Moderation Stats" subtitle="Content health and review pipeline status" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Review Status Breakdown */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Review Status Pipeline</p>
            <div className="space-y-3">
              {[
                { label: 'Total Reviews', value: 1247, pct: 100, color: 'bg-gray-200', text: 'text-gray-700' },
                { label: 'Approved', value: 1089, pct: 87, color: 'bg-green-400', text: 'text-green-700' },
                { label: 'Pending Review', value: 112, pct: 9, color: 'bg-amber-400', text: 'text-amber-700' },
                { label: 'Flagged', value: 31, pct: 2.5, color: 'bg-red-400', text: 'text-red-600' },
                { label: 'Rejected', value: 15, pct: 1.2, color: 'bg-gray-400', text: 'text-gray-500' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">{s.label}</span>
                    <span className={`text-xs font-bold ${s.text}`}>{s.value.toLocaleString()}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${s.color} rounded-full`} style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Star Distribution Pie */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Rating Distribution</p>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={reviewDistribution} cx="50%" cy="50%" innerRadius={50} outerRadius={75} paddingAngle={3} dataKey="value">
                    {reviewDistribution.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => [`${v}%`, 'Share']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-5 gap-1 mt-2">
              {reviewDistribution.map((d) => (
                <div key={d.name} className="text-center">
                  <div className="w-2 h-2 rounded-full mx-auto mb-1" style={{ backgroundColor: d.color }} />
                  <p className="text-[10px] text-gray-500">{d.name.split(' ')[0]}</p>
                  <p className="text-xs font-bold text-gray-700">{d.value}%</p>
                </div>
              ))}
            </div>
          </div>

          {/* Moderation Queue */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Moderation Queue</p>
            <div className="space-y-3">
              {[
                { label: 'Inappropriate Content', count: 12, icon: '🚫', color: 'bg-red-50 text-red-600 border-red-100' },
                { label: 'Fraud / Fake Review', count: 8, icon: '🕵️', color: 'bg-orange-50 text-orange-600 border-orange-100' },
                { label: 'Host Dispute', count: 6, icon: '⚖️', color: 'bg-amber-50 text-amber-600 border-amber-100' },
                { label: 'Spam / Off-topic', count: 5, icon: '📢', color: 'bg-yellow-50 text-yellow-600 border-yellow-100' },
              ].map((item) => (
                <div key={item.label} className={`flex items-center justify-between px-3 py-2.5 rounded-xl border ${item.color}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-base">{item.icon}</span>
                    <span className="text-xs font-semibold">{item.label}</span>
                  </div>
                  <span className="text-sm font-bold">{item.count}</span>
                </div>
              ))}
              <div className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100 mt-1">
                <span className="text-xs font-bold text-gray-700">Total Queued</span>
                <span className="text-sm font-bold text-gray-900">31</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Bottom Row: Top Properties + Recent Activity ── */}
      <section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Top Properties */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <SectionHeader title="Top Performing Properties" subtitle="By bookings this month" />
            <div className="space-y-3">
              {topProperties.map((p, i) => (
                <div key={p.name} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-100 text-gray-600' : 'bg-orange-50 text-orange-600'
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{p.name}</p>
                    <p className="text-xs text-gray-400">{p.host}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-gray-700">{p.revenue}</p>
                    <p className="text-[10px] text-gray-400">{p.bookings} bookings · ⭐ {p.rating}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <SectionHeader title="Recent Activity" subtitle="Latest platform events" />
            <div className="space-y-0">
              {recentActivity.map((item, i) => (
                <div key={i} className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 bg-gray-50 rounded-xl flex items-center justify-center text-sm">
                      {item.icon}
                    </div>
                    <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${item.dot}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 leading-snug">{item.text}</p>
                  </div>
                  <span className="text-[10px] text-gray-400 flex-shrink-0 mt-0.5">{item.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
